'use client';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { AddIcon } from '@/shared/ui/display/EmojiIcon';
import type {
  AttrDef,
  AttributeCondition,
  ConditionEffect,
  ConditionRule,
} from '../model/moving-item.types';
import { SPEC_LEVEL_ATTR_INDEX, SPEC_LEVEL_ATTR_LABEL } from '../model/moving-item.types';
import { ConditionRow } from './ConditionRow';

/** 스펙 공통 조건은 소유 속성이 없어 편집기에 넘길 가짜 정의가 필요하다 */
const SPEC_LEVEL_ATTR_DEF: AttrDef = {
  key: '',
  name: SPEC_LEVEL_ATTR_LABEL,
  type: 'STRING',
};

function resolveRowKey(
  cond: AttributeCondition,
  attrIndex: number,
  condIndex: number,
  keyCounts: Map<string, number>
): string {
  const u = cond._clientRowKey;
  if (u && (keyCounts.get(u) ?? 0) === 1) return u;
  return `${attrIndex}-${condIndex}`;
}

export interface ConditionRuleListProps {
  attributes: AttrDef[];
  /** 속성에 종속되지 않는 스펙 공통 조건 */
  specConditions?: AttributeCondition[];
  onAddCondition: (attrIndex: number) => void;
  onRemoveCondition: (attrIndex: number, condIndex: number) => void;
  onUpdateConditionRule: (
    attrIndex: number,
    condIndex: number,
    updates: Partial<ConditionRule>
  ) => void;
  onUpdateConditionEffect: (
    attrIndex: number,
    condIndex: number,
    updates: Partial<ConditionEffect>
  ) => void;
  onMoveCondition?: (fromAttrIndex: number, condIndex: number, toAttrIndex: number) => void;
}

type FlatEntry = {
  attrIndex: number;
  condIndex: number;
  attr: AttrDef;
  condition: AttributeCondition;
};

export function ConditionRuleList({
  attributes,
  specConditions = [],
  onAddCondition,
  onRemoveCondition,
  onUpdateConditionRule,
  onUpdateConditionEffect,
  onMoveCondition,
}: ConditionRuleListProps) {
  const entries = useMemo(() => {
    const out: FlatEntry[] = [];
    specConditions.forEach((condition, condIndex) => {
      out.push({
        attrIndex: SPEC_LEVEL_ATTR_INDEX,
        condIndex,
        attr: SPEC_LEVEL_ATTR_DEF,
        condition,
      });
    });
    attributes.forEach((attr, attrIndex) => {
      (attr.conditions ?? []).forEach((condition, condIndex) => {
        out.push({ attrIndex, condIndex, attr, condition });
      });
    });
    return out;
  }, [attributes, specConditions]);

  const clientRowKeyCounts = useMemo(() => {
    const m = new Map<string, number>();
    for (const c of entries) {
      const u = c.condition._clientRowKey;
      if (u) m.set(u, (m.get(u) ?? 0) + 1);
    }
    return m;
  }, [entries]);

  const defaultAddAttrIndex = useMemo(() => {
    const i = attributes.findIndex((a) => a.type === 'NUMBER' && a.key.trim());
    if (i >= 0) return i;
    return attributes.length > 0 ? 0 : SPEC_LEVEL_ATTR_INDEX;
  }, [attributes]);

  const [activeEditRowKey, setActiveEditRowKey] = useState<string | null>(null);
  const pendingAddTargetRef = useRef(SPEC_LEVEL_ATTR_INDEX);
  const pendingMoveOpenRef = useRef<number | null>(null);

  const setExclusiveEditRowKey = useCallback((key: string | null) => {
    setActiveEditRowKey(key);
  }, []);

  const conditionsOf = useCallback(
    (attrIndex: number) =>
      attrIndex === SPEC_LEVEL_ATTR_INDEX
        ? specConditions
        : (attributes[attrIndex]?.conditions ?? []),
    [attributes, specConditions]
  );

  const entryKeys = useMemo(
    () =>
      entries.map(({ attrIndex, condIndex, condition }) =>
        resolveRowKey(condition, attrIndex, condIndex, clientRowKeyCounts)
      ),
    [entries, clientRowKeyCounts]
  );

  useEffect(() => {
    if (
      activeEditRowKey &&
      activeEditRowKey !== 'pending' &&
      !entryKeys.includes(activeEditRowKey)
    ) {
      setActiveEditRowKey(null);
    }
  }, [entryKeys, activeEditRowKey]);

  const handleAddRule = () => {
    pendingAddTargetRef.current = defaultAddAttrIndex;
    onAddCondition(defaultAddAttrIndex);
    setActiveEditRowKey('pending');
  };

  useEffect(() => {
    if (activeEditRowKey !== 'pending') return;
    const idx = pendingAddTargetRef.current;
    const list = conditionsOf(idx);
    const last = list[list.length - 1];
    if (last) {
      setActiveEditRowKey(resolveRowKey(last, idx, list.length - 1, clientRowKeyCounts));
    }
  }, [conditionsOf, activeEditRowKey, clientRowKeyCounts]);

  useEffect(() => {
    const to = pendingMoveOpenRef.current;
    if (to === null) return;
    const list = conditionsOf(to);
    const last = list[list.length - 1];
    if (last) {
      setActiveEditRowKey(resolveRowKey(last, to, list.length - 1, clientRowKeyCounts));
      pendingMoveOpenRef.current = null;
    }
  }, [conditionsOf, clientRowKeyCounts]);

  const hint =
    attributes.length === 0
      ? '속성이 없어도 "스펙 공통" 규칙은 추가할 수 있습니다. 이 규칙은 항상 참으로 평가되어 가이드 메시지·서비스 제한을 걸 때 사용합니다.'
      : '규칙은 선택한 평가 속성에 저장됩니다. 속성 무관하게 항상 적용하려면 평가 속성을 "스펙 공통"으로 두세요. 숫자 비교는 NUMBER 타입 속성(예: inch)을 선택하세요.';

  const showEmptyPlaceholder = entries.length === 0;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 0.75,
        flex: 1,
        minHeight: showEmptyPlaceholder ? { xs: 120, md: 160 } : undefined,
      }}
    >
      <Typography
        variant="caption"
        color="text.secondary"
        noWrap
        title={hint}
        sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
      >
        {hint}
      </Typography>

      {showEmptyPlaceholder && (
        <Box
          sx={{
            flex: 1,
            minHeight: 48,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 1,
            border: 1,
            borderColor: 'divider',
            borderStyle: 'dashed',
            px: 1,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            아직 규칙이 없습니다. 아래에서 추가하세요.
          </Typography>
        </Box>
      )}

      {entries.map(({ attrIndex, condIndex, attr, condition: cond }) => {
        const rowKey = resolveRowKey(cond, attrIndex, condIndex, clientRowKeyCounts);
        return (
          <ConditionRow
            key={rowKey}
            rowKey={rowKey}
            exclusiveEditRowKey={activeEditRowKey}
            setExclusiveEditRowKey={setExclusiveEditRowKey}
            condition={cond}
            ownerAttrDef={attr}
            ownerAttrIndex={attrIndex}
            attrValues={attr.type === 'ENUM' ? attr.values?.filter(Boolean) : undefined}
            allAttrDefs={attributes}
            onUpdateRule={(updates) => onUpdateConditionRule(attrIndex, condIndex, updates)}
            onUpdateEffect={(updates) => onUpdateConditionEffect(attrIndex, condIndex, updates)}
            onRemove={() => onRemoveCondition(attrIndex, condIndex)}
            onMoveEvalAttr={
              onMoveCondition
                ? (to) => {
                    if (to === attrIndex) return;
                    onMoveCondition(attrIndex, condIndex, to);
                    pendingMoveOpenRef.current = to;
                  }
                : undefined
            }
          />
        );
      })}

      <Button
        size="small"
        startIcon={<AddIcon />}
        onClick={handleAddRule}
        sx={{ alignSelf: 'flex-start' }}
      >
        규칙 추가
      </Button>
    </Box>
  );
}
